import { isServerSide } from 'utils/host-config';
import { ClientInfoPageDataSchema, ClientInfoPageErrorSchema } from './schema';
import type { ClientInfoPageData } from './types';

/**
 * Gets the agent-admin API host based on environment
 */
export function getAgentAdminHost(): string {
  const envHost = process.env.AGENT_ADMIN_API_HOST;
  if (envHost) {
    return envHost;
  }

  // Default hosts by environment — logged so a missing AGENT_ADMIN_API_HOST in a real
  // deployment is visible in server logs rather than silently pointing at the wrong host.
  console.warn('[ClientInfoPages] AGENT_ADMIN_API_HOST is not set — falling back to a hardcoded default host');

  if (process.env.NODE_ENV === 'production') {
    return 'https://agents.exprealty.com';
  }

  // For local development, default to localhost:3000 (agent-admin default port)
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }

  return 'https://agents-staging.exprealty.com';
}

/**
 * Gets the API key for agent-admin
 */
function getApiKey(): string {
  return process.env.AGENT_ADMIN_API_KEY || '';
}

/**
 * Fetches a client info page by UUID from agent-admin
 *
 * @param uuid The unique identifier for the client info page
 * @param signal Optional AbortSignal for cancellation
 * @returns The client info page data, or null if not found
 */
export async function fetchClientInfoPage(
  uuid: string,
  signal?: AbortSignal
): Promise<ClientInfoPageData | null> {
  if (!isServerSide()) {
    console.warn('fetchClientInfoPage should only be called server-side');
    return null;
  }

  const host = getAgentAdminHost();
  const apiKey = getApiKey();
  const isDevelopment = process.env.NODE_ENV === 'development';

  if (!apiKey && !isDevelopment) {
    console.error('AGENT_ADMIN_API_KEY is not configured');
    return null;
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Only add X-API-Key header if we have one (matches what agent-admin expects)
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    console.log(`[ClientInfoPages] Fetching ${host}/api/v1/client_info_pages/${uuid}`);

    const response = await fetch(`${host}/api/v1/client_info_pages/${uuid}`, {
      method: 'GET',
      headers,
      signal,
    });

    // Check content type to ensure we're getting JSON, not HTML
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (response.status === 404) {
      console.warn(`[ClientInfoPages] Page ${uuid} not found (404) - may have been deleted or never existed`);
      return null;
    }

    if (response.status === 401) {
      console.error('[ClientInfoPages] Unauthorized (401) - API key may be missing or invalid');
      if (!isJson) {
        const errorText = await response.text();
        console.error('[ClientInfoPages] Received HTML instead of JSON. Response preview:', errorText.substring(0, 500));
      }
      return null;
    }

    if (response.status === 410) {
      // Page has expired
      if (isJson) {
        const errorParsed = ClientInfoPageErrorSchema.safeParse(await response.json());
        console.info(`[ClientInfoPages] Page ${uuid} has expired:`, errorParsed.success ? errorParsed.data.error : 'unknown reason');
      } else {
        console.warn(`[ClientInfoPages] Page ${uuid} expired but received non-JSON response`);
      }
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      if (!isJson) {
        console.error(`[ClientInfoPages] Received HTML error page instead of JSON (status ${response.status}). This usually means the API endpoint has an error. Response preview:`, errorText.substring(0, 1000));
      } else {
        console.error(`[ClientInfoPages] Failed to fetch client info page: ${response.status} ${response.statusText}`, errorText);
      }
      return null;
    }

    if (!isJson) {
      const errorText = await response.text();
      console.error('[ClientInfoPages] Expected JSON but received HTML. Response preview:', errorText.substring(0, 1000));
      return null;
    }

    const raw: unknown = await response.json();
    const parsed = ClientInfoPageDataSchema.safeParse(raw);
    if (!parsed.success) {
      console.error(`[ClientInfoPages] Response for ${uuid} did not match expected shape:`, parsed.error.message);
      return null;
    }
    return parsed.data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.info('Client info page fetch was aborted');
      return null;
    }

    console.error('Error fetching client info page:', error);
    return null;
  }
}

/**
 * Validates if a string is a valid UUID format
 *
 * @param str The string to validate
 * @returns True if the string is a valid UUID
 */
export function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}
